import React, {lazy, memo, useEffect, useRef, useState} from 'react';
import {useToast} from "../../common/fusion-ui/provider/ToastProvider";
import {checkNullArr, checkNullStr, createTimeout, getElementHeight, getJsonValue, isBoolTrue, multSize, setTabTitle} from "../../common/fusion-ui/utils/ReactUtils";
import {printError, printLog} from "../../common/CommonUtils";
import {AdminInterface, ModuleTable, TableSkeleton} from "../components";
import {CssVariant, IconClass, Messages} from "../../common/fusion-ui/config/FusionConfig";
import {sendAdminRequest} from "../utils/AdminUtils";
import {AdminModuleFormReqUrls, AdminModules} from "../config/AdminConfig";
import useScreenSize from "../../common/fusion-ui/hook/useScreenSize";
import {ShowViewType} from "../../app/config/AppConfig";
import {ThemedButton, ThemedIconButton} from "../../common/components";
import {Label} from "../../common/fusion-ui";
import {AnimationType, FramerAnimator} from "../../common/fusion-ui/animation/FramerAnimator";

//Lazy imports (to reduce load)
const ParentCompany__AddEditFormPage = lazy(() => import('../forms/ParentCompany__AddEditFormPage'));
const Group__AddEditFormPage = lazy(() => import('../forms/Group__AddEditFormPage'));
const Country__AddEditFormPage = lazy(() => import('../forms/Country__AddEditFormPage'));
const State__AddEditFormPage = lazy(() => import('../forms/State__AddEditFormPage'));
const City__AddEditFormPage = lazy(() => import('../forms/City__AddEditFormPage'));
const ExpSpeaks__AddEditFormPage = lazy(() => import('../forms/ExpSpeaks__AddEditFormPage'));

/**
 * Component: ModuleTabularPage
 *
 * This component is responsible for rendering a tabular interface for a
 * specific module, which includes functionalities like fetching data,
 * displaying a loading state, rendering a header component, and handling
 * add/edit forms.
 *
 * Dependencies:
 * - `useToast()`: Custom hook to show toast messages.
 * - `useScreenSize()`: Custom hook to detect screen size adjustments.
 *
 * State:
 * - fieldsData: State object to manage various fields like loading status,
 *   fetched data, etc.
 * - showAddFormPopup: Boolean state to track the visibility of the add form popup.
 *
 * Effects:
 * - Sets the document title based on the module name and triggers initial data
 *   fetch with a timeout.
 *
 * Methods:
 * - fetchTableData: Fetches and updates the table data from the provided URL.
 * - renderAddEditForms: Conditionally renders different add/edit form components
 *   based on the module name.
 *
 * Usage:
 * This component is meant to be used within an admin interface to manage lists of
 * various module entities, providing functionalities to add, edit, and refresh records.
 *
 * @param {Object} props
 * @param {string} [props.moduleName] - The name of the module, used for setting titles and API endpoints.
 * @param {string} [props.pageDescription] - Description about the page.
 * @param {string} [props.fetchRecordsUrl] -  The URL endpoint to fetch records data for the table.
 * @param {string} [props.requestOptions] -  Additional request-options for table-data fetch request (Optional).
 * @param {boolean} [props.showMultipleDelete=true] -  Whether to show multiple-delete button.
 * @param {function} [props.onValueChange] -  Callback function to handle state changes when table values are updated.
 * @param {React.Element || JSX.Element} [props.headerComponent] -  Optional, a component to be rendered at the top of the table.
 */
const ModuleTabularPage = memo(({
                                    moduleName,
                                    showMultipleDelete = false,
                                    fetchRecordsUrl,
                                    onValueChange,
                                    headerComponent,
                                    pageDescription,
                                    requestOptions = {},
                                }) => {
    const TAG = "ModuleTabularPage";

    const {showToast, changeToastVariant} = useToast();
    const {isScreenUpSm, isScreenUpMd} = useScreenSize();
    const animator = new FramerAnimator();

    const Fields = {
        recordList: `recordList`,

        isDataFetched: 'isDataFetched',
        isDataLoading: 'isDataLoading',
        dataLoadingMsg: 'dataLoadingMsg',
        isDataFound: 'isDataFound',

    };
    const [fieldsData, setFieldsData] = useState({
        [Fields.isDataLoading]: true,
    });
    const headerRef = useRef(null);

    const [showAddFormPopup, setShowAddFormPopup] = useState(false);

    useEffect(() => {
        setTabTitle(moduleName);
        createTimeout(() => fetchTableData(), 500);
    }, [moduleName]);


    return (<>
        <AdminInterface moduleName={moduleName} tag={TAG}>
            <div>
                {/*pageDescription*/}
                {checkNullStr(pageDescription) &&
                    animator.setAnimationType(AnimationType.SLIDE_UP).setDelay(100).renderChildren(
                        <div>
                            {/*page-description*/}
                            <Label className={'text-theme-resp fs-md w-100 px-1x mb-3'} textAt={'start'}>
                                {pageDescription}
                            </Label>
                        </div>
                    )}

                {headerComponent && <div ref={headerRef}>
                    {headerComponent}
                </div>}

                <div className={`position-relative`}>
                    {/*header*/}
                    <div className={`${isScreenUpSm ? 'mb-3 d-flex w-auto' : 'position-absolute'}`}
                         style={{
                             top: (headerComponent ? `-${multSize(getElementHeight(headerRef.current, 'px'), 2.8)}` : '-2.2rem'), right: '-.25rem', zIndex: 10
                         }}>
                        <div className={`d-flex w-100 al-center ${isScreenUpMd ? 'jc-start' : 'jc-end'}`}>
                            {/*refresh-btn*/}
                            {<ThemedIconButton
                                className={'fs-md'}
                                tooltip={`Refresh Table`}
                                icon={IconClass.refresh}
                                outlined={true}
                                borderWidth={0}
                                size={'24px'}
                                mr={3}
                                disabled={isFieldTrue(Fields.isDataLoading)}
                                onClick={() => fetchTableData(false)}
                            />}

                            {/*add-btn*/}
                            {checkNullStr(getAddPageUrl()) && <ThemedButton
                                className={'w-auto fs-sm px-2 py-1 rounded-2 text-nowrap'}
                                text={`Add ${moduleName}`}
                                outlined={true}
                                height={'24px'}
                                disabled={isFieldTrue(Fields.isDataLoading)}
                                href={getAddPageUrl()}
                                onClick={(e) => {
                                    //Show popup for some specific module-forms
                                    if (!isScreenUpSm && (moduleName == AdminModules.ParentCompany || moduleName == AdminModules.Groups || moduleName == AdminModules.Country || moduleName == AdminModules.State || moduleName == AdminModules.City || moduleName == AdminModules.ExpertSpeaks)) {
                                        e.preventDefault();
                                        if (e.ctrlKey) {
                                            window.open(getAddPageUrl(), '_blank');
                                        } else {
                                            setShowAddFormPopup(true);
                                        }
                                    }
                                }}
                            />}
                        </div>
                    </div>

                    <div className={''}>
                        {isFieldTrue(Fields.isDataLoading)
                            ? <TableSkeleton/>
                            : <ModuleTable
                                moduleName={moduleName}
                                showMultipleDelete={showMultipleDelete}
                                onValueChange={async (updatedRows, triggerRefresh) => {
                                    // log('ModuleTable.onValueChange.updatedRows:',updatedRows,'triggerRefresh:', triggerRefresh);
                                    if (isBoolTrue(triggerRefresh)) {
                                        await fetchTableData(false);
                                    }
                                    if (onValueChange) onValueChange(updatedRows);
                                }}>
                                {getField(Fields.recordList)}
                            </ModuleTable>}
                    </div>
                </div>
            </div>
        </AdminInterface>
        {renderAddEditForms()}
    </>)

    function renderAddEditForms() {
        switch (moduleName) {
            case AdminModules.ParentCompany:
                return <ParentCompany__AddEditFormPage{...__createProps()}/>
            case AdminModules.Groups:
                return <Group__AddEditFormPage{...__createProps()}/>
            case AdminModules.Country:
                return <Country__AddEditFormPage{...__createProps()}/>
            case AdminModules.State:
                return <State__AddEditFormPage{...__createProps()}/>
            case AdminModules.City:
                return <City__AddEditFormPage{...__createProps()}/>
            case AdminModules.ExpertSpeaks:
                return <ExpSpeaks__AddEditFormPage{...__createProps(ShowViewType.DRAWER)}/>
            // case AdminModules.GroupsMembers:
            //     return <Group__AddEditMembersFormPage{...__createProps()}/>
            default:
                return null;
        }

        function __createProps(showAs = ShowViewType.DIALOG) {
            return {
                showAs: showAs, open: showAddFormPopup, onSuccess: (async () => {
                    setShowAddFormPopup(false);
                    if (onValueChange) onValueChange(getField(Fields.recordList));
                    await fetchTableData(false);
                }), onHide: (() => setShowAddFormPopup(false)),
            }
        }

    }

    async function fetchTableData(checkBefore = true) {
        const fun = 'fetchTableData:';
        if (!checkBefore || (checkBefore && !isFieldTrue(Fields.isDataFetched))) {
            updateField(Fields.isDataLoading, true);
            updateField(Fields.isDataFound, false);
            updateField(Fields.isDataFetched, true);
            updateField(Fields.dataLoadingMsg, null);
            updateField(Fields.recordList, []);
            try {
                //send-request
                const REQUEST_OPTIONS = {...requestOptions};
                sendAdminRequest({
                    reqUrl: fetchRecordsUrl, reqOptions: REQUEST_OPTIONS, onFetched: ((fetchedData) => {
                        log(fun, 'moduleName:', moduleName, 'fetchedData:', fetchedData);
                        const recordList = getJsonValue(fetchedData, 'data', []);
                        const success = checkNullArr(recordList);
                        updateField(Fields.isDataFound, success);
                        if (success) {
                            // createTimeout(() => {
                            updateField(Fields.recordList, recordList);
                            updateField(Fields.isDataLoading, false);
                            // }, 3000);
                        } else {
                            updateField(Fields.dataLoadingMsg, Messages.noDataFound);
                            toast(Messages.noDataFound, CssVariant.danger);
                            updateField(Fields.isDataLoading, false);
                        }
                    }), onError: ((error) => {
                        logErr(fun, error);
                        updateField(Fields.isDataLoading, false);
                        updateField(Fields.isDataFound, false);
                        updateField(Fields.dataLoadingMsg, Messages.fetchError);
                    }),
                });
            } catch (error) {
                logErr(fun, error);
                updateField(Fields.isDataLoading, false);
                updateField(Fields.isDataFound, false);
                updateField(Fields.dataLoadingMsg, Messages.fetchError);
            }
        }
    }

    function getAddPageUrl() {
        return getJsonValue(AdminModuleFormReqUrls[moduleName], 'add')
    }

    function updateField(field, value) {
        setFieldsData(prevState => ({...prevState, [field]: value}));
    }

    function getField(field, defValue) {
        return getJsonValue(fieldsData, field, defValue);
    }

    function isFieldTrue(field) {
        return isBoolTrue(getField(field), true, true);
    }

    function toast(text, variant) {
        showToast(text + '');
        changeToastVariant(variant);
    }

    function log(...text) {
        printLog(TAG, ...text);
    }

    function logErr(...text) {
        printError(TAG, ...text);
    }
});
export default ModuleTabularPage;
